// src/api/educaSimulatorAPI.js
import { ENV } from "../Utils";

/* ===========================
 * Helpers
 * =========================== */
function buildSimulatorFormData(values) {
  const fd = new FormData();

  // Campos texto
  if (values.title != null) fd.append("title", values.title);
  if (values.description != null) fd.append("description", values.description);
  if (values.idServer != null) fd.append("idServer", values.idServer);

  // tag es string
  if (values.tag !== undefined) fd.append("tag", values.tag);

  // 'data'
  if (Array.isArray(values.data)) {
    fd.append("data", values.data.join(","));
  } else if (values.data != null) {
    fd.append("data", String(values.data));
  }

  // Fichero opcional
  if (values.miniatureFile) {
    fd.append("miniature", values.miniatureFile);
  }

  return fd;
}

/* ===========================
 * Simulator API
 * =========================== */

export async function createSimulator(accessToken, values) {
  const url = `${ENV.BASE_API}/${ENV.API_ROUTES.SIMULATORS}`;
  const body = buildSimulatorFormData(values);

  const res = await fetch(url, {
    method: "POST",
    headers: { Authorization: `Bearer ${accessToken}` },
    body,
  });
  const json = await res.json();
  if (!res.ok) throw json;
  return json;
}

export const getSimulators = async (params, accessToken) => {
  const pageFilter = `page=${params?.page || 1}`;
  const limitFilter = `limit=${params?.limit || 10}`;
  const url = `${ENV.BASE_API}/${ENV.API_ROUTES.SIMULATORS}?${pageFilter}&${limitFilter}`;

  const headers = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
  const response = await fetch(url, { headers });
  const result = await response.json();
  if (response.status !== 200) throw result;
  return result;
};

export const getSimulatorById = async (accessToken, id) => {
  const url = `${ENV.BASE_API}/${ENV.API_ROUTES.SIMULATORS}/${id}`;
  const headers = { "Content-Type": "application/json" };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  const res = await fetch(url, { method: "GET", headers });
  const result = await res.json();
  if (res.status !== 200) throw result;
  return result;
};

export async function updateSimulator(accessToken, simulatorId, values) {
  const url = `${ENV.BASE_API}/${ENV.API_ROUTES.SIMULATORS}/${simulatorId}`;
  const body = buildSimulatorFormData(values);

  const res = await fetch(url, {
    method: "PATCH",
    headers: { Authorization: `Bearer ${accessToken}` },
    body,
  });
  const json = await res.json();
  if (!res.ok) throw json;
  return json;
}

export const deleteSimulator = async (accessToken, simulatorId) => {
  const url = `${ENV.BASE_API}/${ENV.API_ROUTES.SIMULATORS}/${simulatorId}`;
  const params = {
    method: "DELETE",
    headers: { Authorization: `Bearer ${accessToken}` },
  };

  const response = await fetch(url, params);
  const result = await response.json();
  if (response.status !== 200) throw result;
  return result;
};

export const simulatorApi = {
  createSimulator,
  getSimulators,
  getSimulatorById,
  updateSimulator,
  deleteSimulator,
};

/* ===========================
 * ServerSimulator API
 * =========================== */

// <--- CAMBIO: Aceptamos visores (default 4)
export const createServer = async (
  accessToken,
  { serverKey, simulatorDB = [], visores = 4 },
) => {
  const url = `${ENV.BASE_API}/${ENV.API_ROUTES.SERVER_SIMULATORS}`;
  const params = {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    // <--- CAMBIO: incluimos visores en el body
    body: JSON.stringify({ serverKey, simulatorDB, visores }),
  };

  const response = await fetch(url, params);
  const result = await response.json();
  if (response.status !== 200) throw result;
  return result;
};

export const getServers = async (params, accessToken) => {
  const pageFilter = `page=${params?.page || 1}`;
  const limitFilter = `limit=${params?.limit || 10}`;
  const url = `${ENV.BASE_API}/${ENV.API_ROUTES.SERVER_SIMULATORS}?${pageFilter}&${limitFilter}`;

  const headers = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
  const response = await fetch(url, { headers });
  const result = await response.json();
  if (response.status !== 200) throw result;
  return result;
};

// <--- NUEVO: Función genérica para actualizar datos del servidor (ej. visores)
export const updateServer = async (accessToken, serverId, data) => {
  // Asumimos ruta PATCH /server-simulators/:id
  const url = `${ENV.BASE_API}/${ENV.API_ROUTES.SERVER_SIMULATORS}/${serverId}`;
  const params = {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  };

  const response = await fetch(url, params);
  const result = await response.json();
  if (response.status !== 200) throw result;
  return result;
};

export const activateServer = async (
  accessToken,
  { serverKey, institutionID },
) => {
  const url = `${ENV.BASE_API}/${ENV.API_ROUTES.SERVER_SIMULATORS_ACTIVATE}`;
  const params = {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ serverKey, institutionID }),
  };

  const response = await fetch(url, params);
  const result = await response.json();
  if (response.status !== 200) throw result;
  return result;
};

export const addSimulators = async (accessToken, serverId, idsParam = []) => {
  const url = `${ENV.BASE_API}/${ENV.API_ROUTES.SERVER_SIMULATORS}/${serverId}/simulators`;

  let ids = idsParam;
  if (!Array.isArray(ids) && ids && Array.isArray(ids.ids)) {
    ids = ids.ids;
  }

  const cleanIds = (Array.isArray(ids) ? ids : [])
    .map((v) => (typeof v === "string" ? v : v?._id))
    .filter(Boolean)
    .map(String);

  if (cleanIds.length === 0) {
    throw new Error("Debes seleccionar al menos un simulador.");
  }

  const params = {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ ids: cleanIds }),
  };

  const response = await fetch(url, params);
  const result = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(result.message || "No se pudo agregar simuladores");
  }

  return result;
};

export const deactivateServer = async (accessToken, serverId) => {
  const url = `${ENV.BASE_API}/${ENV.API_ROUTES.SERVER_SIMULATORS}/${serverId}/deactivate`;
  const params = {
    method: "PATCH",
    headers: { Authorization: `Bearer ${accessToken}` },
  };

  const response = await fetch(url, params);
  const result = await response.json();
  if (response.status !== 200) throw result;
  return result;
};

export const serverSimulatorApi = {
  createServer,
  getServers,
  activateServer,
  addSimulators,
  deactivateServer,
  updateServer, // <--- Exportamos la nueva función
};
